import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const KlasemenPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedCompetitionName, setSelectedCompetitionName] = useState('');
  const [standingsData, setStandingsData] = useState([]);
  const [loadingStandings, setLoadingStandings] = useState(false);
  
  // Team detail modal state
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetail, setTeamDetail] = useState(null);
  const [loadingTeamDetail, setLoadingTeamDetail] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [loadingTeamPlayers, setLoadingTeamPlayers] = useState(false);
  const [teamModalTab, setTeamModalTab] = useState('players');
  
  // Player detail modal state
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerDetail, setPlayerDetail] = useState(null);
  const [loadingPlayerDetail, setLoadingPlayerDetail] = useState(false);
  
  // Paywall state
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [tokensBalance, setTokensBalance] = useState(0);
  const [unlockCost, setUnlockCost] = useState(25);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCompetitions();
    checkUnlockStatus();
  }, []);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/lapangbola/competitions`);
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        setCompetitions(response.data.data);
        const firstComp = response.data.data[0];
        setSelectedCompetition(firstComp.id);
        setSelectedCompetitionName(firstComp.name);
        fetchStandings(firstComp.id);
      } else if (response.data.error) {
        toast.error('Endpoint kompetisi belum tersedia');
        setCompetitions([]);
      } else {
        setCompetitions([]);
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error('Gagal memuat data kompetisi');
    } finally {
      setLoading(false);
    }
  };

  const fetchStandings = async (competitionId) => {
    try {
      setLoadingStandings(true);
      const response = await axios.get(`${API}/api/lapangbola/classements-list`, {
        params: { competition_id: competitionId }
      });
      
      if (response.data && response.data.data && response.data.status === 'success') {
        setStandingsData(response.data.data);
      } else if (response.data.error || response.data.status === 'error') {
        toast.error(response.data.message || 'Data klasemen belum tersedia');
        setStandingsData([]);
      } else {
        setStandingsData([]);
      }
    } catch (error) {
      console.error('Error fetching standings:', error);
      toast.error('Gagal memuat klasemen');
      setStandingsData([]);
    } finally {
      setLoadingStandings(false);
    }
  };

  const checkUnlockStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/statistik/unlock-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsUnlocked(response.data.unlocked);
      setTokensBalance(response.data.tokens_balance);
      setUnlockCost(response.data.unlock_cost);
    } catch (error) {
      console.error('Error checking unlock status:', error);
    }
  };

  const handleUnlock = async () => {
    if (tokensBalance < unlockCost) {
      toast.error(`Token tidak cukup! Anda butuh ${unlockCost} token, saldo: ${tokensBalance} token`);
      return;
    }
    
    setUnlocking(true);
    try {
      const response = await axios.post(`${API}/api/statistik/unlock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsUnlocked(true);
      setTokensBalance(response.data.tokens_balance);
      setShowUnlockModal(false);
      toast.success('Fitur statistik berhasil di-unlock!');
    } catch (error) {
      console.error('Error unlocking:', error);
      toast.error(error.response?.data?.detail || 'Gagal unlock fitur statistik');
    } finally {
      setUnlocking(false);
    }
  };

  const handleCompetitionChange = (competitionId) => {
    const comp = competitions.find(c => c.id === Number(competitionId));
    setSelectedCompetition(Number(competitionId));
    setSelectedCompetitionName(comp?.name || '');
    fetchStandings(Number(competitionId));
  };

  // Handle team click to open detail modal
  const handleTeamClick = async (team) => {
    setSelectedTeam({
      club_id: team.club_id,
      club_name: team.club_name,
      club_picture: team.picture_url
    });
    setTeamModalTab('players');
    setLoadingTeamDetail(true);
    setLoadingTeamPlayers(true);
    setTeamPlayers([]);
    setTeamDetail(null);
    
    try {
      const [playersResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/api/lapangbola/club-detail/${team.club_id}?competition_id=${selectedCompetition}`),
        axios.get(`${API}/api/lapangbola/club-detail/${team.club_id}`)
      ]);
      
      if (playersResponse.data?.data?.[0]?.player_list) {
        setTeamPlayers(playersResponse.data.data[0].player_list);
      }
      
      if (statsResponse.data?.data?.[0]) {
        setTeamDetail(statsResponse.data.data[0]);
      }
    } catch (error) {
      console.error('Error fetching team detail:', error);
      toast.error('Gagal memuat detail tim');
    } finally {
      setLoadingTeamDetail(false);
      setLoadingTeamPlayers(false);
    }
  };

  const closeTeamModal = () => {
    setSelectedTeam(null);
    setTeamDetail(null);
    setTeamPlayers([]);
    setTeamModalTab('players');
  };

  // Handle clicking player from team modal
  const handleTeamPlayerClick = async (player) => {
    // Check if unlocked before showing player detail
    if (!isUnlocked) {
      setShowUnlockModal(true);
      return;
    }
    
    closeTeamModal();
    setSelectedPlayer({
      player_id: player.id,
      player_name: player.name,
      player_picture: player.picture_url || null
    });
    setLoadingPlayerDetail(true);
    setPlayerDetail(null);
    
    try {
      const response = await axios.get(`${API}/api/lapangbola/players/${player.id}`);
      if (response.data) {
        setPlayerDetail(response.data);
      } else {
        toast.error('Detail pemain tidak tersedia');
        setSelectedPlayer(null);
      }
    } catch (error) {
      console.error('Error fetching player detail:', error);
      toast.error('Gagal memuat detail pemain');
      setSelectedPlayer(null);
    } finally {
      setLoadingPlayerDetail(false);
    }
  };

  const closePlayerModal = () => {
    setSelectedPlayer(null);
    setPlayerDetail(null);
  };

  // Get form badge color
  const getFormColor = (result) => {
    switch(result) {
      case 'W': return 'bg-green-500';
      case 'D': return 'bg-gray-400';
      case 'L': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Header */}
      <header className="bg-gradient-ltb-r text-white py-4 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/')}
                className="text-white hover:text-white/80 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold">Klasemen</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Competition Selector */}
        <div className="mb-4">
          <select
            value={selectedCompetition || ''}
            onChange={(e) => handleCompetitionChange(Number(e.target.value))}
            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-ltb-bright focus:border-transparent text-gray-800 font-medium"
          >
            {competitions.length === 0 ? (
              <option>Tidak ada kompetisi</option>
            ) : (
              competitions.map(comp => (
                <option key={comp.id} value={comp.id}>
                  {comp.name}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Standings */}
        {loadingStandings ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto mb-4"></div>
            <p className="text-gray-600">Memuat klasemen...</p>
          </div>
        ) : standingsData.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-600 text-lg">Data klasemen belum tersedia</p>
          </div>
        ) : (
          <div className="space-y-4">
            {standingsData.map((stage, stageIdx) => (
              <div key={stageIdx}>
                {stage.groups && stage.groups.map((group, groupIdx) => (
                  <div key={groupIdx} className="bg-white rounded-xl shadow-md overflow-hidden mb-4">
                    {/* Group Header */}
                    <div className="bg-ltb-bright text-white px-4 py-3 flex items-center justify-between">
                      <h3 className="font-bold text-lg">{group.name}</h3>
                      <span className="text-white/80 text-sm">{stage.name}</span>
                    </div>

                    {/* Scrollable Table Container */}
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px]">
                        {/* Table Header */}
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr className="text-xs font-semibold text-gray-500 uppercase">
                            <th className="px-3 py-2 text-center w-12">#</th>
                            <th className="px-3 py-2 text-left">Tim</th>
                            <th className="px-3 py-2 text-center w-12">M</th>
                            <th className="px-3 py-2 text-center w-12">W</th>
                            <th className="px-3 py-2 text-center w-12">D</th>
                            <th className="px-3 py-2 text-center w-12">L</th>
                            <th className="px-3 py-2 text-center w-16">GD</th>
                            <th className="px-3 py-2 text-center w-14">Pts</th>
                            <th className="px-3 py-2 text-center w-32">Form</th>
                          </tr>
                        </thead>

                        {/* Table Body */}
                        <tbody className="divide-y divide-gray-100">
                          {group.classments && group.classments.map((team, teamIdx) => (
                            <tr 
                              key={team.club_id || teamIdx}
                              onClick={() => handleTeamClick(team)}
                              className={`hover:bg-blue-50 cursor-pointer transition-colors ${
                                teamIdx === 0 ? 'bg-green-50' : 
                                teamIdx === 1 ? 'bg-green-50/50' : ''
                              }`}
                            >
                              {/* Position */}
                              <td className="px-3 py-3 text-center">
                                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                  team.position === 1 ? 'bg-yellow-400 text-yellow-900' :
                                  team.position === 2 ? 'bg-gray-300 text-gray-700' :
                                  team.position === 3 ? 'bg-orange-300 text-orange-800' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {team.position}
                                </span>
                              </td>

                              {/* Team Info */}
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                                    {team.picture_url ? (
                                      <img 
                                        src={team.picture_url} 
                                        alt={team.club_name}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><circle cx="12" cy="12" r="10"/></svg>';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs font-bold">
                                        {team.club_short_name || team.club_name?.charAt(0) || '?'}
                                      </div>
                                    )}
                                  </div>
                                  <p className="font-semibold text-gray-800 text-sm whitespace-nowrap">{team.club_name}</p>
                                </div>
                              </td>

                              {/* Played */}
                              <td className="px-3 py-3 text-center text-sm font-medium text-gray-700">
                                {team.play || 0}
                              </td>

                              {/* Wins */}
                              <td className="px-3 py-3 text-center text-sm font-medium text-green-600">
                                {team.win || 0}
                              </td>

                              {/* Draws */}
                              <td className="px-3 py-3 text-center text-sm font-medium text-gray-500">
                                {team.draw || 0}
                              </td>

                              {/* Losses */}
                              <td className="px-3 py-3 text-center text-sm font-medium text-red-600">
                                {team.lose || 0}
                              </td>

                              {/* Goal Difference */}
                              <td className="px-3 py-3 text-center text-sm font-medium">
                                <span className={team.gd > 0 ? 'text-green-600' : team.gd < 0 ? 'text-red-600' : 'text-gray-600'}>
                                  {team.gd > 0 ? '+' : ''}{team.gd || 0}
                                </span>
                              </td>

                              {/* Points */}
                              <td className="px-3 py-3 text-center">
                                <span className="inline-flex items-center justify-center w-8 h-8 bg-ltb-bright text-white rounded-lg text-sm font-bold">
                                  {team.point || 0}
                                </span>
                              </td>

                              {/* Form */}
                              <td className="px-3 py-3">
                                <div className="flex justify-center gap-1">
                                  {team.forms && team.forms.slice(-5).map((form, formIdx) => (
                                    <span 
                                      key={formIdx}
                                      className={`w-5 h-5 rounded-full ${getFormColor(form)} text-white text-xs font-bold flex items-center justify-center`}
                                    >
                                      {form}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Scroll hint for mobile */}
                    <div className="bg-gray-50 px-3 py-2 text-xs text-gray-500 sm:hidden border-t text-center">
                      ← Geser untuk melihat semua kolom →
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Team Detail Modal */}
      {selectedTeam && (
        <div 
          className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4"
          onClick={closeTeamModal}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Team Name */}
            <div className="bg-gradient-ltb-r text-white p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  {selectedTeam.club_picture && (
                    <img 
                      src={selectedTeam.club_picture} 
                      alt={selectedTeam.club_name}
                      className="w-12 h-12 rounded-lg bg-white/20 object-cover"
                    />
                  )}
                  <div>
                    <h3 className="font-bold text-xl uppercase tracking-wide">
                      {selectedTeam.club_name}
                    </h3>
                    <p className="text-white/80 text-sm">Detail Tim</p>
                  </div>
                </div>
                <button 
                  onClick={closeTeamModal}
                  className="p-1 hover:bg-white/20 rounded-full"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setTeamModalTab('players')}
                className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                  teamModalTab === 'players'
                    ? 'text-ltb-bright border-b-2 border-ltb-bright bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Pemain ({teamPlayers.length})
              </button>
              <button
                onClick={() => setTeamModalTab('stats')}
                className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                  teamModalTab === 'stats'
                    ? 'text-ltb-bright border-b-2 border-ltb-bright bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Statistik
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-160px)]">
              {/* Players Tab */}
              {teamModalTab === 'players' && (
                <div className="p-4">
                  {loadingTeamPlayers ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ltb-bright mx-auto mb-3"></div>
                      <p className="text-gray-500">Memuat daftar pemain...</p>
                    </div>
                  ) : teamPlayers.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">Tidak ada data pemain</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {teamPlayers.map((player, index) => (
                        <div
                          key={player.id}
                          onClick={() => handleTeamPlayerClick(player)}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-blue-50 cursor-pointer transition-colors relative"
                        >
                          {/* Rank Number */}
                          <div className="w-8 h-8 flex items-center justify-center bg-ltb-bright text-white rounded-full text-sm font-bold">
                            {index + 1}
                          </div>
                          
                          {/* Player Photo */}
                          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                            {player.picture_url ? (
                              <img 
                                src={player.picture_url} 
                                alt={player.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><circle cx="12" cy="8" r="4"/><path d="M12 14c-4 0-8 2-8 4v2h16v-2c0-2-4-4-8-4z"/></svg>';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg font-bold">
                                {player.name?.charAt(0) || '?'}
                              </div>
                            )}
                          </div>
                          
                          {/* Player Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800 truncate">{player.name}</p>
                            <p className="text-xs text-gray-500">{player.position || '-'}</p>
                          </div>
                          
                          {/* Jersey Number */}
                          {player.number && (
                            <div className="bg-ltb-bright text-white px-3 py-1 rounded-lg text-sm font-bold">
                              #{player.number}
                            </div>
                          )}
                          
                          {/* Lock indicator if not unlocked */}
                          {!isUnlocked && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Stats Tab */}
              {teamModalTab === 'stats' && (
                <div className="p-4">
                  {loadingTeamDetail ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ltb-bright mx-auto mb-3"></div>
                      <p className="text-gray-500">Memuat detail tim...</p>
                    </div>
                  ) : teamDetail ? (
                    <div>
                      {/* Summary Stats Row */}
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-gray-400 text-[10px]">Games Played</p>
                          <p className="font-bold text-ltb-bright text-xl">{teamDetail.play || 0}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-gray-400 text-[10px]">Wins</p>
                          <p className="font-bold text-ltb-bright text-xl">{teamDetail.win || 0}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-gray-400 text-[10px]">Losses</p>
                          <p className="font-bold text-ltb-bright text-xl">{teamDetail.lose || 0}</p>
                        </div>
                      </div>

                      {/* Attack Section */}
                      <div className="mb-4">
                        <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Attack</h5>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Shots</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.shot || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Shot Off Target</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.shot_off_target || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Shot On Target</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.shot_on_target || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Goals</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.goal || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Chances Created</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.create_chance || 0}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Team Play Section */}
                      <div className="mb-4">
                        <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Team Play</h5>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Total Pass</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.pass || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Pass Success</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.pass_success || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Pass Accuracy</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.pass_accuracy || 0}%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Defence Section */}
                      <div className="mb-4">
                        <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Defence</h5>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Defence Success</td>
                              <td className="py-2 text-right font-semibold text-gray-800">
                                {(teamDetail.tackle || 0) + (teamDetail.intercept || 0) + (teamDetail.block || 0)}
                              </td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Tackles</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.tackle || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Interceptions</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.intercept || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Blocks</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.block || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Saves</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.save || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Penalty Saves</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.penalty_save || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Cross Claims</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.cross_claim || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Goals Conceded</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.concede || 0}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Discipline Section */}
                      <div>
                        <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Discipline</h5>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Fouls</td>
                              <td className="py-2 text-right font-semibold text-gray-800">{teamDetail.foul || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Yellow Cards</td>
                              <td className="py-2 text-right font-semibold text-yellow-600">{teamDetail.yellow_card || 0}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-600">Red Cards</td>
                              <td className="py-2 text-right font-semibold text-red-600">{teamDetail.red_card || 0}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500">Detail tim tidak tersedia</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Player Detail Modal */}
      {selectedPlayer && (
        <div 
          className="fixed inset-0 bg-black/70 z-[80] flex items-center justify-center p-4"
          onClick={closePlayerModal}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-ltb-r text-white p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <span className="bg-white/20 px-3 py-1 rounded text-sm font-semibold">Detail Pemain</span>
                </div>
                <button 
                  onClick={closePlayerModal}
                  className="p-1 hover:bg-white/20 rounded-full"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <h3 className="font-bold text-xl mt-2">{selectedPlayer.player_name}</h3>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-4">
              {loadingPlayerDetail ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ltb-bright mx-auto mb-3"></div>
                  <p className="text-gray-500">Memuat detail pemain...</p>
                </div>
              ) : playerDetail ? (
                <div className="flex flex-col sm:flex-row">
                  {/* Left Panel - Player Info */}
                  <div className="sm:w-2/5 bg-gray-50 p-4">
                    <div className="w-full aspect-square max-w-[180px] mx-auto rounded-lg overflow-hidden bg-gray-200 border-4 border-white shadow-lg mb-4">
                      {playerDetail.picture_url || selectedPlayer.player_picture ? (
                        <img 
                          src={playerDetail.picture_url || selectedPlayer.player_picture} 
                          alt={playerDetail.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><circle cx="12" cy="8" r="4"/><path d="M12 14c-4 0-8 2-8 4v2h16v-2c0-2-4-4-8-4z"/></svg>';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-5xl font-bold bg-gradient-to-br from-blue-100 to-blue-200">
                          {playerDetail.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>

                    <div className="w-full space-y-2 text-sm">
                      <div>
                        <p className="text-gray-400 text-xs">Nationality</p>
                        <p className="font-bold text-gray-800">{playerDetail.nationality || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Date of Birth</p>
                        <p className="font-bold text-gray-800">{playerDetail.date_of_birth || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Age</p>
                        <p className="font-bold text-gray-800">{playerDetail.age || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Height</p>
                        <p className="font-bold text-gray-800">{playerDetail.height ? `${playerDetail.height} cm` : '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Weight</p>
                        <p className="font-bold text-gray-800">{playerDetail.weight ? `${playerDetail.weight} kg` : '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Primary Position</p>
                        <p className="font-bold text-gray-800">{playerDetail.position || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Jersey</p>
                        <p className="font-bold text-gray-800">{playerDetail.number || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Right Panel - Stats */}
                  <div className="sm:w-3/5 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-ltb-bright text-white px-3 py-1 rounded text-sm font-semibold">Stats</span>
                    </div>

                    {/* Summary Stats Row */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="text-gray-400 text-[10px]">Appearances</p>
                        <p className="font-bold text-ltb-bright text-lg">{playerDetail.total_play || 0}</p>
                        <p className="text-gray-400 text-[10px]">/ {playerDetail.total_sub || '--'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="text-gray-400 text-[10px]">Wins</p>
                        <p className="font-bold text-ltb-bright text-lg">
                          {playerDetail.match_histories?.filter(m => {
                            const isHome = m.club === m.home_club;
                            return isHome ? m.home_score > m.away_score : m.away_score > m.home_score;
                          }).length || 0}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="text-gray-400 text-[10px]">Losses</p>
                        <p className="font-bold text-ltb-bright text-lg">
                          {playerDetail.match_histories?.filter(m => {
                            const isHome = m.club === m.home_club;
                            return isHome ? m.home_score < m.away_score : m.away_score < m.home_score;
                          }).length || 0}
                        </p>
                      </div>
                    </div>

                    {/* Attack Section */}
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Attack</h5>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Shots</td>
                            <td className="py-2 text-right font-semibold text-gray-800">
                              {(playerDetail.shot_on_target || 0) + (playerDetail.shot_off_target || 0) + (playerDetail.shot_blocked || 0)}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Shot On Target</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.shot_on_target || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Goals</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.total_goal || 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Team Play Section */}
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Team Play</h5>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Passing Success</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.pass_success || playerDetail.pass || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Assist</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.assist || 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Defence Section */}
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Defence</h5>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Tackles</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.tackle || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Intercepts</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.intercept || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Blocks</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.block || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Saves</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.save || 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Discipline Section */}
                    <div>
                      <h5 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-gray-200 pb-1">Discipline</h5>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Fouls</td>
                            <td className="py-2 text-right font-semibold text-gray-800">{playerDetail.foul || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Yellow Cards</td>
                            <td className="py-2 text-right font-semibold text-yellow-600">{playerDetail.yellow_card || 0}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-gray-600">Red Cards</td>
                            <td className="py-2 text-right font-semibold text-red-600">{playerDetail.red_card || 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">Detail pemain tidak tersedia</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unlock Modal */}
      {showUnlockModal && (
        <div 
          className="fixed inset-0 bg-black/70 z-[90] flex items-center justify-center p-4"
          onClick={() => setShowUnlockModal(false)}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-sm overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-ltb-r text-white p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold">Unlock Detail Pemain</h3>
              <p className="text-white/80 text-sm mt-2">Bayar token untuk melihat statistik lengkap semua pemain</p>
            </div>
            
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Biaya Unlock</span>
                  <span className="font-bold text-ltb-bright">{unlockCost} Token</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Saldo Anda</span>
                  <span className={`font-bold ${tokensBalance >= unlockCost ? 'text-green-600' : 'text-red-600'}`}>
                    {tokensBalance} Token
                  </span>
                </div>
              </div>
              
              {tokensBalance < unlockCost && (
                <p className="text-red-500 text-sm text-center mb-4">
                  Token tidak cukup. Silakan top up terlebih dahulu.
                </p>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUnlockModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50"
                >
                  Batal
                </button>
                {tokensBalance >= unlockCost ? (
                  <button
                    onClick={handleUnlock}
                    disabled={unlocking}
                    className="flex-1 px-4 py-3 bg-gradient-ltb-r text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    {unlocking ? 'Memproses...' : 'Unlock Sekarang'}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowUnlockModal(false);
                      navigate('/topup');
                    }}
                    className="flex-1 px-4 py-3 bg-gradient-ltb-r text-white rounded-xl font-medium hover:opacity-90"
                  >
                    Top Up Token
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KlasemenPage;
